import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "shared/contexts/I18nContext";

const KebabMenu = ({
  onEdit,
  editLabel,
  onDelete,
  deleteLabel,
  onArchive,
  archiveLabel,
  onUnarchive,
  unarchiveLabel,
  onEvaluations, // New prop for the evaluations action
  onAssignToBatch,
  onRevoke,
  revokeLabel = "Revoke",
  menuItems = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const { t } = useTranslation();

  const toggleMenu = (e) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleMenu}
        className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
      >
        {/* Kebab menu icon (three dots) */}
        <svg
          className="w-5 h-5 text-gray-600 dark:text-gray-300"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z"></path>
        </svg>
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-700 rounded-md shadow-lg z-10">
          <ul className="py-1">
            {onEdit && (
              <li>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  {editLabel || t("edit")}
                </button>
              </li>
            )}
            {onEvaluations && ( // Display the item only if the onEvaluations prop is provided
              <li>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEvaluations();
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  {t("evaluations")}
                </button>
              </li>
            )}
            {onAssignToBatch && (
              <li>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssignToBatch();
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  {t("assignToBatch")}
                </button>
              </li>
            )}
            {/* The existing menuItems prop will handle any other dynamic menu items passed to the component. */}
            {menuItems.map((item, index) => (
              <li key={index}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    item.onClick();
                    setIsOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 ${item.isDestructive ? "text-red-700 dark:text-red-400" : ""
                    }`}
                >
                  {item.label}
                </button>
              </li>
            ))}
            {onRevoke && (
              <li>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRevoke();
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900"
                >
                  {revokeLabel}
                </button>
              </li>
            )}
            {onUnarchive && (
              <li>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnarchive();
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900"
                >
                  {unarchiveLabel || t("unarchive") || "Restore to Active"}
                </button>
              </li>
            )}
            {onArchive && (
              <li>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive();
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900"
                >
                  {archiveLabel || t("archive") || "Archive"}
                </button>
              </li>
            )}
            {onDelete && (
              <li>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900"
                >
                  {deleteLabel || t("delete")}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default KebabMenu;